import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import { ArrowSyncCheckmarkRegular } from "@fluentui/react-icons";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import React from "react";
import { useTranslation } from "react-i18next";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";
import { ImportedInvoiceImportResult } from "types/importedInvoices";

// Re-match links the entries of the given invoices (importedInvoiceIds: one row, or every
// ticked row) or of every invoice that is not fully matched (null) to the installments by
// VIN, insurer, insurance line and premium. It never clears a bank statement entry: that
// stays a manual step on the Bank statements page.
export const RematchImportedInvoicesDialog = () => {
  const [
    { rematchImportedInvoicesDialog, rematchImportedInvoicesStatus },
    {
      setImportedInvoicesStoreValue,
      setImportedInvoicesStoreValues,
      rematchImportedInvoices,
      applyImportedInvoices,
    },
  ] = useImportedInvoicesStore();
  const { t } = useTranslation();
  const isLoading = rematchImportedInvoicesStatus === "loading";
  const ids: number[] | null = rematchImportedInvoicesDialog.importedInvoiceIds;
  const isAll = ids === null;
  const isMany = !isAll && ids.length > 1;

  const title = isAll
    ? t("pct.rematchAllImportedInvoices.label")
    : isMany
    ? t("pct.rematchImportedInvoicesSelected.label", { n: ids.length })
    : t("pct.rematchImportedInvoice.label");
  const confirm = isAll
    ? t("pct.rematchAllImportedInvoicesConfirm.label")
    : isMany
    ? t("pct.rematchImportedInvoicesSelectedConfirm.label", { n: ids.length })
    : t("pct.rematchImportedInvoiceConfirm.label");

  return (
    <Dialog open={rematchImportedInvoicesDialog.isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {confirm}
            <VSpace height={20} />
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isLoading}
              onClick={() => {
                setImportedInvoicesStoreValue("rematchImportedInvoicesDialog", {
                  isOpen: false,
                  importedInvoiceIds: null,
                });
              }}
            >
              {t("pct.close.label")}
            </Button>
            <Button
              data-help="imported-invoices-rematch-confirm"
              appearance="primary"
              disabled={isLoading}
              icon={
                isLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowSyncCheckmarkRegular fontSize={16} />
                )
              }
              onClick={() => {
                rematchImportedInvoices(
                  { importedInvoiceIds: ids },
                  (result: ImportedInvoiceImportResult) => {
                    setImportedInvoicesStoreValues({
                      rematchImportedInvoicesDialog: {
                        isOpen: false,
                        importedInvoiceIds: null,
                      },
                    });
                    toast.success(
                      t("pct.rematchImportedInvoicesResult.label", {
                        matched: result.matchedEntryCount,
                        unmatched: result.unmatchedEntryCount,
                      })
                    );
                    applyImportedInvoices({
                      importedInvoices: result.importedInvoices,
                      t,
                    });
                  }
                );
              }}
            >
              {t("pct.rematch.label")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
